import { ApiService } from '../../shared/services/core/api.service';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { Worksheet } from './models/worksheet.model';
import { WorksheetStore } from './worksheet.store';
import { WorksheetQuery } from './worksheet.query';
import { LoggingService } from '../../shared/services/core/logging.service';
import { tap, mapTo } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { config } from '../../shared/config';

@Injectable({ providedIn: 'root' })
export class WorksheetService {
  constructor(
    private api: ApiService,
    private worksheetStore: WorksheetStore,
    private worksheetQuery: WorksheetQuery,
    private logService: LoggingService,
    private http: HttpClient
  ) {}

  /**
   * Gets a worksheet by Id
   *
   * @param worksheetId worksheet Id
   */
  public fetchWorksheetIdId(worksheetId: number): Observable<Worksheet | undefined> {
    if (this.worksheetQuery.hasEntity(worksheetId)) {
      return this.worksheetQuery.selectEntity(worksheetId);
    } else {
      return this.api.getById<Worksheet>('worksheet', worksheetId.toString()).pipe(
        tap((worksheet) => {
          this.worksheetStore.add(worksheet);
        })
      );
    }
  }

  /**
   * Gets all worksheets by url
   *
   * @param route route of the current page
   */
  public fetchWorksheetByRoute(route: string) {
    this.worksheetStore.setLoading(true);
    this.logService.info('making call for worksheets');

    const request$: Observable<null> = this.api.get<Worksheet>('worksheet/route?route=' + route).pipe(
      tap((worksheet) => {
        if (worksheet.length == 0) {
          worksheet = [{ WorksheetId: 0, RouteUrl: route }] as Worksheet[];
        }
        this.worksheetStore.add(worksheet);
        this.worksheetStore.setLoading(false);
      }),
      mapTo(null)
    );

    return this.worksheetQuery.hasRoute(route) ? of(null) : request$;
  }

  /**
   * Will download a worksheet and log this download
   *
   * @param worksheetId the id of the worksheet
   * @param worksheetItemId the item id of the worksheet
   */
  public downloadWorksheet(worksheetId: number, worksheetItemId: number): Observable<Blob> {
    return this.http.get<Blob>(`${config.baseApiUrl}/api/worksheet/download/` + worksheetId + '/' + worksheetItemId, {
      headers: this.api.getHeaders(),
      responseType: 'blob' as 'json',
    });
  }

  /**
   * Logs that the customer has started watching the stream
   *
   * @param streamKey stream key
   */
  public logViewStreamStart(streamKey: string) {
    let headers = this.api.getHeaders();
    let streamToken = localStorage.getItem('streamToken');
    if (streamToken) {
      localStorage.removeItem('streamToken');
    }

    return this.http
      .post<string>(`${config.baseApiUrl}/api/worksheet/view/` + streamKey, null, { headers: headers })
      .pipe(
        tap((token) => {
          localStorage.setItem('streamToken', token);
        })
      )
      .toPromise();
  }

  /**
   * Logs that the customer is still watching the stream
   *
   * @param streamKey stream key
   */
  public logStreamWatching(streamKey: string) {
    let headers = this.api.getHeaders();
    let streamToken = localStorage.getItem('streamToken');
    if (streamToken) {
      headers = headers.append('streamToken', streamToken);
    }

    return this.http
      .post<string>(`${config.baseApiUrl}/api/worksheet/view/` + streamKey, null, { headers: headers })
      .pipe(
        tap((token) => {
          localStorage.setItem('streamToken', token);
        })
      )
      .toPromise();
  }
}
